import React, { useState } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Label } from '@/components/ui/label';
import { Switch } from '@/components/ui/switch';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Checkbox } from '@/components/ui/checkbox';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { PEOOrganizationService } from '@/services/peo-organization.service';
import { useToast } from '@/hooks/use-toast';
import { PEOOrganizationalRole, ROLE_TYPE_LABELS } from '@/types/peo-organization.types';

interface BulkPermissionDialogProps {
  providerId: string;
  children: React.ReactNode;
}

interface BulkPermissionUpdate {
  roleIds: string[];
  permissions: {
    can_view_applications?: boolean;
    can_create_applications?: boolean;
    can_edit_applications?: boolean;
    can_delete_applications?: boolean;
    can_view_quotes?: boolean;
    can_create_quotes?: boolean;
    can_edit_quotes?: boolean;
    can_delete_quotes?: boolean;
    can_view_analytics?: boolean;
    can_manage_team?: boolean;
    can_manage_roles?: boolean;
  };
}

export const BulkPermissionDialog: React.FC<BulkPermissionDialogProps> = ({
  providerId,
  children
}) => {
  const [open, setOpen] = useState(false);
  const [selectedRoleIds, setSelectedRoleIds] = useState<string[]>([]);
  const [permissions, setPermissions] = useState({
    can_view_applications: false,
    can_create_applications: false,
    can_edit_applications: false,
    can_delete_applications: false,
    can_view_quotes: false,
    can_create_quotes: false,
    can_edit_quotes: false,
    can_delete_quotes: false,
    can_view_analytics: false,
    can_manage_team: false,
    can_manage_roles: false,
  });

  const { toast } = useToast();
  const queryClient = useQueryClient();

  const { data: roles, isLoading } = useQuery({
    queryKey: ['organizational-roles', providerId],
    queryFn: () => PEOOrganizationService.getOrganizationalRoles(providerId),
    enabled: open && !!providerId
  });

  const bulkUpdatePermissions = useMutation({
    mutationFn: async (updates: BulkPermissionUpdate) => {
      // Update permissions for each selected role
      const updatePromises = updates.roleIds.map(roleId => 
        PEOOrganizationService.updateOrganizationalRole(roleId, updates.permissions)
      );
      
      return await Promise.all(updatePromises);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['organizational-roles', providerId] });
      toast({
        title: 'Success',
        description: `Updated permissions for ${selectedRoleIds.length} role${selectedRoleIds.length !== 1 ? 's' : ''}`,
      });
      setOpen(false);
      resetForm();
    },
    onError: (error) => {
      toast({
        title: 'Error',
        description: `Failed to update permissions: ${error.message}`,
        variant: 'destructive',
      });
    },
  });

  const resetForm = () => {
    setSelectedRoleIds([]);
    setPermissions({
      can_view_applications: false,
      can_create_applications: false,
      can_edit_applications: false,
      can_delete_applications: false,
      can_view_quotes: false,
      can_create_quotes: false,
      can_edit_quotes: false,
      can_delete_quotes: false,
      can_view_analytics: false,
      can_manage_team: false,
      can_manage_roles: false,
    });
  };

  const handleRoleToggle = (roleId: string, checked: boolean) => {
    if (checked) {
      setSelectedRoleIds(prev => [...prev, roleId]);
    } else {
      setSelectedRoleIds(prev => prev.filter(id => id !== roleId));
    }
  };

  const handleSelectAll = (checked: boolean) => {
    if (checked && roles) {
      setSelectedRoleIds(roles.map(role => role.id));
    } else {
      setSelectedRoleIds([]);
    }
  };

  const handlePermissionChange = (permission: keyof typeof permissions, value: boolean) => {
    setPermissions(prev => ({
      ...prev,
      [permission]: value
    }));
  };

  const handleSubmit = () => {
    if (selectedRoleIds.length === 0) {
      toast({
        title: 'Validation Error',
        description: 'Please select at least one role to update',
        variant: 'destructive',
      });
      return;
    }

    // Only include permissions that are set to true
    const activePermissions = Object.entries(permissions)
      .filter(([_, value]) => value)
      .reduce((acc, [key, value]) => ({ ...acc, [key]: value }), {});

    if (Object.keys(activePermissions).length === 0) {
      toast({
        title: 'Validation Error',
        description: 'Please select at least one permission to update',
        variant: 'destructive',
      });
      return;
    }

    bulkUpdatePermissions.mutate({
      roleIds: selectedRoleIds,
      permissions: activePermissions,
    });
  };

  const permissionOptions = [
    { key: 'can_view_applications', label: 'View Applications', description: 'Allow viewing of all applications' },
    { key: 'can_create_applications', label: 'Create Applications', description: 'Allow creating new applications' },
    { key: 'can_edit_applications', label: 'Edit Applications', description: 'Allow editing existing applications' },
    { key: 'can_delete_applications', label: 'Delete Applications', description: 'Allow deleting applications' },
    { key: 'can_view_quotes', label: 'View Quotes', description: 'Allow viewing of all quotes' },
    { key: 'can_create_quotes', label: 'Create Quotes', description: 'Allow creating new quotes' },
    { key: 'can_edit_quotes', label: 'Edit Quotes', description: 'Allow editing existing quotes' },
    { key: 'can_delete_quotes', label: 'Delete Quotes', description: 'Allow deleting quotes' },
    { key: 'can_view_analytics', label: 'View Analytics', description: 'Allow access to analytics and reports' },
    { key: 'can_manage_team', label: 'Manage Team', description: 'Allow team member management' },
    { key: 'can_manage_roles', label: 'Manage Roles', description: 'Allow role and permission management' },
  ];

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        {children}
      </DialogTrigger>
      <DialogContent className="sm:max-w-[800px] max-h-[80vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>Bulk Edit Permissions</DialogTitle>
        </DialogHeader>

        <div className="space-y-6">
          {/* Role Selection */}
          <Card>
            <CardHeader>
              <CardTitle className="text-lg">Select Roles</CardTitle>
              <CardDescription>
                Choose which roles you want to update permissions for
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              {isLoading ? (
                <div className="space-y-2">
                  {Array.from({ length: 3 }).map((_, i) => (
                    <div key={i} className="h-12 bg-muted rounded animate-pulse" />
                  ))}
                </div>
              ) : !roles || roles.length === 0 ? (
                <p className="text-muted-foreground">No roles found</p>
              ) : (
                <>
                  <div className="flex items-center space-x-2 pb-2 border-b">
                    <Checkbox
                      id="select-all"
                      checked={selectedRoleIds.length === roles.length}
                      onCheckedChange={handleSelectAll}
                    />
                    <Label htmlFor="select-all" className="font-medium">
                      Select All ({roles.length} roles)
                    </Label>
                  </div>
                  
                  <div className="space-y-2 max-h-40 overflow-y-auto">
                    {roles.map((role) => (
                      <div key={role.id} className="flex items-center space-x-2">
                        <Checkbox
                          id={`role-${role.id}`}
                          checked={selectedRoleIds.includes(role.id)}
                          onCheckedChange={(checked) => handleRoleToggle(role.id, checked as boolean)}
                        />
                        <Label htmlFor={`role-${role.id}`} className="flex-1 cursor-pointer">
                          <div className="flex items-center gap-2">
                            <span className="font-medium">{role.role_name}</span>
                            <Badge variant="outline" className="text-xs">
                              {ROLE_TYPE_LABELS[role.role_type]}
                            </Badge>
                          </div>
                          {role.description && (
                            <p className="text-sm text-muted-foreground mt-1">
                              {role.description}
                            </p>
                          )}
                        </Label>
                      </div>
                    ))}
                  </div>
                  
                  {selectedRoleIds.length > 0 && (
                    <div className="mt-2 p-2 bg-primary/5 rounded">
                      <p className="text-sm">
                        <strong>{selectedRoleIds.length}</strong> role{selectedRoleIds.length !== 1 ? 's' : ''} selected
                      </p>
                    </div>
                  )}
                </>
              )}
            </CardContent>
          </Card>

          {/* Permission Selection */}
          <Card>
            <CardHeader>
              <CardTitle className="text-lg">Select Permissions</CardTitle>
              <CardDescription>
                Choose which permissions to grant to the selected roles
              </CardDescription>
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {permissionOptions.map((option) => (
                  <div key={option.key} className="flex items-start space-x-2">
                    <Switch
                      id={option.key}
                      checked={permissions[option.key as keyof typeof permissions]}
                      onCheckedChange={(checked) => handlePermissionChange(option.key as keyof typeof permissions, checked)}
                    />
                    <div className="space-y-1">
                      <Label htmlFor={option.key} className="text-sm font-medium cursor-pointer">
                        {option.label}
                      </Label>
                      <p className="text-xs text-muted-foreground">
                        {option.description}
                      </p>
                    </div>
                  </div>
                ))}
              </div>

              {Object.values(permissions).some(Boolean) && (
                <div className="mt-4 p-3 bg-muted rounded-lg">
                  <p className="text-sm font-medium mb-2">Permissions to be granted:</p>
                  <div className="flex flex-wrap gap-1">
                    {Object.entries(permissions)
                      .filter(([_, value]) => value)
                      .map(([key, _]) => {
                        const option = permissionOptions.find(opt => opt.key === key);
                        return (
                          <Badge key={key} variant="secondary" className="text-xs">
                            {option?.label}
                          </Badge>
                        );
                      })}
                  </div>
                </div>
              )}
            </CardContent>
          </Card>
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={() => setOpen(false)}>
            Cancel
          </Button>
          <Button 
            onClick={handleSubmit}
            disabled={bulkUpdatePermissions.isPending || selectedRoleIds.length === 0 || !Object.values(permissions).some(Boolean)}
          >
            {bulkUpdatePermissions.isPending ? 'Updating...' : `Update ${selectedRoleIds.length} Role${selectedRoleIds.length !== 1 ? 's' : ''}`}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};